import { useState, useEffect, useCallback } from 'react';

export function useOrderDetailsData(orderId, backendUrl, showPopup) {
  const [order, setOrder] = useState(null); // Includes id, orderName, email, publicAccessKey
  const [files, setFiles] = useState([]);
  const [pdfMetadatas, setPdfMetadatas] = useState([]);
  const [allFonts, setAllFonts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [templates, setTemplates] = useState([]); // List of template project files

  const [editedQuantities, setEditedQuantities] = useState({}); // Initialized after fetching files
  const [editedEmail, setEditedEmail] = useState(""); // Initialized after fetching order

  const [pdfMissingFonts, setPdfMissingFonts] = useState({});

  const [isLoadingOrder, setIsLoadingOrder] = useState(true); // Loading state for initial order fetch
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  // Note: Loading/error for fonts and statuses are not explicitly tracked, could be added if needed.

  // --- Data Fetching Functions ---

  const fetchOrderData = useCallback(async () => {
    if (!orderId || !backendUrl) return;

    setIsLoadingOrder(true);
    // showPopup({ title: "Ładowanie danych", message: "Pobieranie szczegółów zamówienia...", type: 'loading' }); // Optional: show loading popup

    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}`, {
        credentials: "include"
      });
      if (!res.ok) {
        throw new Error("Nie udało się pobrać danych zamówienia (res.ok=false).");
      }
      const data = await res.json();

      // Set order state correctly using customOrderId
      setOrder({
        id: data.id,
        orderName: data.orderName,
        customOrderId: data.customOrderId, // Use customOrderId here
        publicAccessKey: data.publicAccessKey,
        // Include other relevant fields if needed, e.g., email if it's separate
        email: data.customOrderId // Keep email field for compatibility if used elsewhere, but primary check should be customOrderId
      });
      const fetchedFiles = data.files || [];
      setFiles(fetchedFiles);

      // Initialize editedQuantities and pass initialAiContent
      const initialQuantities = {};
      const filesWithContent = fetchedFiles.map(file => {
        initialQuantities[file.id] = file.quantity ?? 1;
        return {
          ...file,
          initialAiContent: file.initialAiContent || ''
        };
      });
      setFiles(filesWithContent);
      setEditedQuantities(initialQuantities);

      setPdfMetadatas(data.pdfMetadatas || []);
      setEditedEmail(data.customOrderId || ""); // Initialize edited email

      // hidePopup(); // Hide loading popup if shown
    } catch (err) {
      console.error("[fetchOrderData] ERROR =>", err);
      showPopup({ title: "Błąd", message: "Błąd pobierania szczegółów zamówienia.", type: 'error' });
      // hidePopup(); // Ensure loading popup is hidden on error
    } finally {
      setIsLoadingOrder(false);
    }
  }, [orderId, backendUrl, showPopup]);

  const fetchAllFonts = useCallback(async () => {
    if (!backendUrl) return;
    try {
      const res = await fetch(`${backendUrl}/admin/fonts`, { credentials: "include" });
      if (!res.ok) throw new Error("Błąd pobierania czcionek.");
      const data = await res.json();
      setAllFonts(data);
    } catch (err) {
      console.error("[OrderDetailsData] fetchAllFonts =>", err);
      // Optionally show error popup
      // showPopup({ title: "Błąd czcionek", message: "Nie udało się pobrać listy czcionek.", type: 'error' });
    }
  }, [backendUrl]); // Removed showPopup dependency unless error popup is desired

  const fetchAllStatuses = useCallback(async () => {
    if (!backendUrl) return;
    try {
      const r = await fetch(`${backendUrl}/admin/statuses`, { credentials: "include" });
      if (!r.ok) throw new Error("Błąd pobierania listy statusów.");
      const data = await r.json();
      setStatuses(data);
    } catch (ex) {
      console.error("[OrderDetailsData][fetchAllStatuses]", ex);
      // Optionally show error popup
      // showPopup({ title: "Błąd statusów", message: "Nie udało się pobrać listy statusów.", type: 'error' });
    }
  }, [backendUrl]); // Removed showPopup dependency unless error popup is desired

  const fetchTemplates = useCallback(async () => {
    if (!backendUrl) return;
    setIsLoadingTemplates(true);
    setTemplates([]);
    try {
      const url = `${backendUrl}/public/templates`; // Using public endpoint
      const res = await fetch(url); // No credentials needed for public
      if (!res.ok) throw new Error(`Błąd pobierania szablonów (public) (status=${res.status})`);
      const templateFiles = await res.json();

      if (Array.isArray(templateFiles)) {
        setTemplates(templateFiles);
      } else {
        console.error("[OrderDetailsData] fetchTemplates => Expected an array but received:", templateFiles);
        setTemplates([]);
        showPopup({ title: "Błąd danych", message: "Otrzymano nieprawidłowe dane szablonów.", type: 'error' });
      }
    } catch (err) {
      console.error("[OrderDetailsData] fetchTemplates => Fetch or processing error:", err);
      showPopup({ title: "Błąd szablonów", message: "Nie udało się pobrać listy dostępnych szablonów.", type: 'error' });
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [backendUrl, showPopup]);

  // --- Effects ---

  // Initial data fetch
  useEffect(() => {
    if (orderId && backendUrl) {
      fetchOrderData();
      fetchAllFonts();
      fetchAllStatuses();
      fetchTemplates();
    }
  }, [orderId, backendUrl, fetchOrderData, fetchAllFonts, fetchAllStatuses, fetchTemplates]);

  // // Removed useEffect that reset editedQuantities on files change, as it caused UI revert issues.
  // // Initialization now happens only inside fetchOrderData.
  // useEffect(() => {
  //   if (files.length > 0) {
  //     const initialQuantities = {};
  //     files.forEach(file => {
  //       initialQuantities[file.id] = file.quantity ?? 1;
  //     });
  //     setEditedQuantities(initialQuantities);
  //   }
  // }, [files]);

  // Compute missing fonts when files or fonts change
  useEffect(() => {
    // Ensure files and allFonts are arrays before proceeding
    if (Array.isArray(files) && Array.isArray(allFonts) && (files.length > 0 || allFonts.length > 0)) {
       const missingMap = {};
       files.forEach((f) => {
        const isPdf = f.fileName.toLowerCase().endsWith(".pdf");
        if (!isPdf) return;

        // Find corresponding metadata using downloadKey
        const metaObj = pdfMetadatas.find((m) => m.downloadKey === f.downloadKey);
        // Ensure metadata exists and has pages
        if (!metaObj || !metaObj.metadata || !metaObj.metadata.pages) return;

        const usedFonts = new Set();
        // Iterate through pages and items (assuming structure from original component)
        metaObj.metadata.pages.forEach((pg) => {
          // Check different potential structures for items (texts, images, svgs might contain font info)
          (pg.items || pg.texts || []).forEach((it) => {
            if (it.fontName) usedFonts.add(it.fontName);
          });
           // Add checks for other item types if necessary
        });


        const missing = [];
        usedFonts.forEach((fontName) => {
          // Case-insensitive comparison
          const found = allFonts.find(
            (af) => af.name.toLowerCase() === fontName.toLowerCase()
          );
          if (!found) missing.push(fontName);
        });

        if (missing.length > 0) {
          missingMap[f.downloadKey] = missing;
        }
      });
      setPdfMissingFonts(missingMap);
    } else {
        setPdfMissingFonts({}); // Reset if no files or fonts
    }
  }, [files, allFonts, pdfMetadatas]); // Depend on files, allFonts, and pdfMetadatas

  return {
    // Data
    order,
    files,
    pdfMetadatas,
    allFonts,
    statuses,
    templates,

    // Editable Data States (managed here, modified by actions hook)
    editedQuantities,
    setEditedQuantities, // Expose setter for actions hook
    editedEmail,
    setEditedEmail, // Expose setter for actions hook

    // Derived Data
    pdfMissingFonts,

    // Loading States
    isLoadingOrder,
    isLoadingTemplates,

    // Setters for data (used by actions hook after updates)
    setOrder,
    setFiles,
    setPdfMetadatas,

    // Refresh function
    fetchOrderData,
    // Expose individual fetchers if needed for specific refreshes
    fetchAllFonts,
    fetchAllStatuses,
    fetchTemplates,
  };
}
